<template>
    <validation-observer ref="form" v-slot="{ meta, setFieldValue, errors }" v-if="!isNil(acceptJS)">
        <busy-modal :show="loading"></busy-modal>
        <card :header-content-class="['w-100']" class="credit-card-container ml-3 mr-3 mb-3">
            <template #header-icon>
                <font-awesome-icon :icon="['fas', 'credit-card']" size="lg" />
            </template>
            <template #header-content>
                <div class="lx-payment__title">
                    Billing Details
                    <font-awesome-icon class="lx-payment__title__icon text-success" :icon="['fas', 'lock']" size="lg" />
                </div>
            </template>
            <template #body>
                <b-row>
                    <b-col md="8" lg="6" class="credit-card-display-container">
                        <billing-card-preview
                            v-if="!isNil(subscriptionCard) && !showEditCard && !isPayNow"
                            :billingCard="billingCard"
                            @toggleCardEdit="toggleCardEdit"
                        />
                        <span v-show="(showEditCard || isNil(subscriptionCard) || isPayNow) && !loading">
                            <b-row class="mt-3" v-if="isProxy">
                                <b-col md="12">
                                    <validation-provider name="channex_card_capture" rules="required|validChannexCard" v-slot="{handleChange}">
                                        <form-channex-card-capture
                                            v-model="channex_card_capture"
                                            ref="capture"
                                            @success-tokenize-card="handleSuccessTokenizeCard"
                                            :auto-validate="true"
                                            @input="handleChange"
                                        />
                                    </validation-provider>
                                </b-col>
                            </b-row>
                            <div v-if="!isProxy">
                                <b-row class="mt-3">
                                    <b-col md="12">
                                        <validation-provider
                                            ref="cardNumber"
                                            name="card_number"
                                            :rules="{
                                                required: true,
                                                validPaymentCardNumber: { validPaymentCardTypes: [] },
                                            }"
                                            v-slot="{handleChange}"
                                        >
                                            <form-input
                                                data-card-field
                                                v-model="cardNumber"
                                                v-mask="cardNumberMask"
                                                floating-label="Card number"
                                                :error="errors.cardNumber"
                                                @input="handleChange"
                                            />
                                        </validation-provider>
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col md="6">
                                        <validation-provider
                                            name="cardExpireDate"
                                            rules="required|validPaymentCardExpiry"
                                            v-slot="{handleChange}"
                                        >
                                            <form-input
                                                data-card-field
                                                v-model="cardExpireDate"
                                                v-mask="cardExpiryMask"
                                                floating-label="MM/YY"
                                                :error="errors.cardExpireDate"
                                                @input="handleChange"
                                            />
                                        </validation-provider>
                                    </b-col>
                                    <b-col md="6">
                                        <validation-provider name="cardCVC" rules="required|validPaymentCardCVC">
                                            <form-input
                                                data-card-field
                                                v-model="cardCVC"
                                                v-mask="cardCVCMask"
                                                floating-label="CVC"
                                                :error="error.cardCVC"
                                                @input="(val) => setFieldValue('cardCVC', val)"
                                            />
                                        </validation-provider>
                                    </b-col>
                                </b-row>
                                <b-row>
                                    <b-col md="12">
                                        <validation-provider
                                            name="cardName"
                                            rules="required|validPaymentCardholderName"
                                            v-slot="{handleChange}"
                                        >
                                            <form-input
                                                data-card-field
                                                v-model="cardName"
                                                floating-label="Cardholder name"
                                                :error="errors.cardName"
                                                @input="handleChange"
                                            />
                                        </validation-provider>
                                    </b-col>
                                </b-row>
                            </div>
                            <b-row v-if="(isPayNow || isTrial) && subscription.package.coupons_allowed">
                                <b-col md="12">
                                    <form-input
                                        floating-label="Promotion Code"
                                        v-model="couponCode"
                                        :rightButton="{
                                            title: '',
                                            text: 'Apply',
                                            variant: 'success',
                                            toggle: false,
                                        }"
                                        @handle-right-button-click="applyCoupon()"
                                    />
                                </b-col>
                            </b-row>
                            <b-row v-if="isPending">
                                <b-col md="12">
                                    <validation-provider
                                        name="agreement"
                                        rules="required:allowFalse"
                                        v-slot="{handleChange}"
                                    >
                                        <form-checkbox @change="handleChange" v-model="agreement" class="agreement-checkbox">
                                            <b
                                                >I have read and understood the
                                                <a href="/terms-of-service/" target="_blank">Terms of Service</a> and
                                                <a href="/privacy-policy/" target="_blank">Privacy Policy</a>.</b
                                            >
                                        </form-checkbox>
                                    </validation-provider>
                                </b-col>
                            </b-row>

                            <b-row class="mt-3">
                                <b-col md="12" class="credit-card-buttons">
                                    <b-button
                                        variant="primary"
                                        v-if="isPayNow"
                                        @click="submitCard"
                                        :disabled="!meta.valid || loading"
                                        size="lg"
                                        ><font-awesome-icon class="mr-2" :icon="['fas', 'money-bill-1']" size="lg" />
                                        Pay Now</b-button
                                    >
                                    <b-button
                                        variant="primary"
                                        v-if="!isPayNow"
                                        @click="submitCard"
                                        :disabled="!meta.valid || loading"
                                        size="lg"
                                        class="mr-2"
                                        ><font-awesome-icon class="mr-2" :icon="['fas', 'floppy-disk']" size="lg" />
                                        Save Card</b-button
                                    >
                                    <b-button
                                        variant="default"
                                        v-if="!isPayNow && !isNil(subscriptionCard)"
                                        @click="toggleCardEdit"
                                        :disabled="loading"
                                        size="lg"
                                        ><font-awesome-icon class="mr-2" :icon="['fas', 'xmark']" size="lg" />
                                        Cancel</b-button
                                    >
                                </b-col>
                            </b-row>
                        </span>
                    </b-col>
                    <billing-card-icons />
                </b-row>
            </template>
        </card>
        <modal
            id="subscription-error-modal"
            title="Subscription Error"
            header-bg-variant="danger"
            header-text-variant="light"
            ok-only
            ><span v-html="errorModalMessage"></span
        ></modal>
    </validation-observer>
</template>

<script>
    import isNil from 'lodash/isNil';
    import { mapActions } from 'vuex';
    import API from '../../../api';
    import BusyModal from '../../generic/busy-modal/busy-modal.vue';
    import Modal from '../../generic/modal/modal.vue';
    import FormInput from '../../generic/form-input/form-input.vue';
    import FormCheckbox from '../../generic/form-checkbox/form-checkbox.vue';
    import Card from '../../generic/card/card.vue';
    import FormChannexCardCapture from '../../generic/form-channex-card-capture/form-channex-card-capture.vue';
    import BillingCardPreview from '../../generic/billing-card-preview/billing-card-preview.vue';
    import BillingCardIcons from '../../generic/billing-card-icons/billing-card-icons.vue';
    import { formatResponseError } from '../../../utils/api';
    import { defineRule as extend } from 'vee-validate';
    import {
        SUBSCRIPTION_BILLING_APPLY_COUPON_ENDPOINT,
        SUBSCRIPTION_BILLING_CANCEL_SUBSCRIPTION_ENDPOINT,
        SUBSCRIPTION_BILLING_SAVE_CARD_ENDPOINT,
        SUBSCRIPTION_BILLING_GET_CARD_ENDPOINT,
        SUBSCRIPTION_BILLING_GATEWAY_ENDPOINT,
    } from '../../../endpoints';

    extend('validChannexCard', (value) => {
        if (value === 'OK') {
            return true;
        }
        return value;
    });

    export default {
        name: 'SubscriptionCreditCardForm',
        components: {
            BusyModal,
            Modal,
            FormInput,
            FormCheckbox,
            Card,
            FormChannexCardCapture,
            BillingCardPreview,
            BillingCardIcons,
        },
        props: {
            subscription: {
                type: Object,
                default: () => ({}),
            },
        },
        computed: {
            isPending() {
                return this.subscription.status === 'CREATED';
            },
            isPayNow() {
                return this.isPending || this.subscription.is_suspended;
            },
            isTrial() {
                return this.subscription.status === 'TRIAL';
            },
            billingCard() {
                return {
                    cardType: this.subscriptionCard?.card_type || null,
                    cardHolder: this.subscriptionCard?.card_holder || null,
                    cardNumber: this.subscriptionCard?.card_no || null,
                    cardExpirationDate: this.subscriptionCard?.card_exp || null,
                    cardCvv: 'XXX' || null,
                };
            },
        },
        data() {
            return {
                loading: true,
                cardNumber: null,
                cardName: null,
                cardExpireDate: null,
                cardExpireMonth: null,
                cardExpireYear: null,
                cardCVC: null,
                cardNumberMask: '#### #### #### ####',
                cardCVCMask: '####',
                cardExpiryMask: '##/##',
                dataDescriptor: null,
                dataValue: null,
                cardToken: null,
                subscriptionCard: null,
                showEditCard: false,
                errorModalMessage: '',
                couponCode: null,
                agreement: null,
                isProxy: false,
                acceptJS: null,
                channex_card_capture: null,
            };
        },
        methods: {
            isNil,
            ...mapActions('accept', ['loadAccept', 'dispatchAcceptData']),
            async fetchGatewayData() {
                try {
                    const response = await API.get(SUBSCRIPTION_BILLING_GATEWAY_ENDPOINT());
                    this.acceptJS = response.data.accept_js;
                    this.isProxy = response.data.is_proxy;
                    if (!this.isProxy) {
                        this.loadAccept(this.acceptJS.accept_js_src_script);
                    }
                } catch (e) {
                    console.log(e);
                    this.errorToast({ text: 'Could not load gateway data.' });
                } finally {
                }
            },
            async submitCard() {
                this.loading = true;
                try {
                    if (this.isProxy) {
                        const response = await this.$refs.capture.$refs.capture.submit();
                    } else {
                        const secureData = {};
                        secureData.authData = {
                            apiLoginID: this.acceptJS.auth_net_user_id,
                            clientKey: this.acceptJS.auth_net_publishable_key,
                        };

                        const [month, year] = this.cardExpireDate.split('/');
                        secureData.cardData = {
                            cardNumber: this.cardNumber.replaceAll(' ', ''),
                            month: month,
                            year: year,
                            cardCode: this.cardCVC,
                            fullName: this.cardName,
                        };

                        const response = await this.dispatchAcceptData(secureData);
                        this.dataDescriptor = response.opaqueData.dataDescriptor;
                        this.dataValue = response.opaqueData.dataValue;

                        this.saveCard();
                    }
                } catch (error) {
                    this.errorToast({ text: 'Something went wrong while confirming payment information.' });
                    this.loading = false;
                }
            },
            handleSuccessTokenizeCard(card) {
                if (/^[0-9\s]*$/.test(card.cardholder_name)) {
                    this.errorToast({ text: 'Error: Invalid card holder.' });
                    this.$refs.capture.$refs.capture.fetchSessionToken();
                    this.loading = false;
                    return;
                }
                this.cardToken = card.card_token;
                this.cardName = card.cardholder_name;
                this.cardExpireMonth = card.expiration_month;
                this.cardExpireYear = card.expiration_year;
                this.saveCard();
                this.$refs.capture.$refs.capture.fetchSessionToken();
            },
            async saveCard() {
                this.loading = true;
                try {
                    await API.post(SUBSCRIPTION_BILLING_SAVE_CARD_ENDPOINT(), {
                        data_descriptor: this.dataDescriptor,
                        data_value: this.dataValue,
                        card_holder: this.cardName,
                        card_expiration_month: this.isProxy ? this.cardExpireMonth : this.cardExpireDate.split('/')[0],
                        card_expiration_year: this.isProxy
                            ? this.cardExpireYear.slice(-2)
                            : this.cardExpireDate.split('/')[1],
                        card_token: this.cardToken,
                    });
                    this.$emit('success-card-saved');
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    if (generic) {
                        this.errorModalMessage = generic;
                        this.subscriptionCard = null;
                        this.$bvModal.show('subscription-error-modal');
                    } else {
                        this.errorToast({ text: 'Something went wrong while saving credit card.' });
                    }
                } finally {
                    this.loading = false;
                    this.dataDescriptor = null;
                    this.dataValue = null;
                    this.cardToken = null;
                    this.cardNumber = null;
                    this.cardName = null;
                    this.cardCVC = null;
                    this.cardExpireDate = null;
                    this.cardExpireMonth = null;
                    this.cardExpireYear = null;
                    this.agreement = null;
                    this.fetchSubscriptionCard();
                }
            },
            async fetchSubscriptionCard() {
                try {
                    this.loading = true;
                    const response = await API.get(SUBSCRIPTION_BILLING_GET_CARD_ENDPOINT());
                    this.subscriptionCard = response.data;
                } catch (e) {
                } finally {
                    this.loading = false;
                    this.showEditCard = false;
                }
            },
            toggleCardEdit() {
                this.showEditCard = !this.showEditCard;
            },
            async applyCoupon() {
                this.loading = true;
                try {
                    await API.put(SUBSCRIPTION_BILLING_APPLY_COUPON_ENDPOINT(), { coupon_code: this.couponCode });
                    this.$emit('success-coupon-applied');
                } catch (error) {
                    const { generic } = formatResponseError(error);
                    this.errorToast({ text: generic || 'Please enter a valid promotion code.' });
                } finally {
                    this.couponCode = null;
                    this.loading = false;
                }
            },
        },
        mounted() {
            this.fetchGatewayData();
            this.fetchSubscriptionCard();
        },
    };
</script>

<style scoped lang="scss">
    @import '~bootstrap/scss/_functions.scss';
    @import '~bootstrap/scss/_variables.scss';
    @import '~bootstrap/scss/mixins/_breakpoints.scss';

    :deep(.agreement-checkbox) {
        font-size: 13px;
    }

    @include media-breakpoint-up(md) {
        :deep(.credit-card-buttons .btn) {
            width: 150px;
            margin-bottom: 0;
        }

        :deep(.credit-card-display-container span div) {
            max-width: 455px;
        }

        :deep(.separator-top) {
            display: none;
        }
    }

    @include media-breakpoint-down(md) {
        :deep(.credit-card-buttons .btn) {
            width: 100%;
            margin-bottom: 10px;
        }

        :deep(.credit-card-display-container span div) {
            max-width: 100%;
        }

        :deep(.separator-top) {
            display: block;
        }
    }

    @include media-breakpoint-down(sm) {
        :deep(.credit-card-display-container span div) {
            max-width: none;
        }
    }

    :deep(*) .lx-payment__title {
        display: flex;
        align-items: center;
        justify-content: space-between;
        &__icon {
            margin-right: 1.5rem;
        }
    }

    :deep(.field__label) {
        font-weight: bold;
        font-size: 50px;
    }
</style>
